/**
 * Unconventional Roles — a closed-off drawer of real, off-the-beaten-path
 * roles from the career corpus (ritual pandit, push-cart vendor, dabbawala…).
 *
 * Presentation only: no scoring. Roles are picked from a curated name list,
 * and ones sharing the learner's primary archetype are shown first.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Compass } from "lucide-react";
import { loadCareersBundle, type CareerRow } from "@/data/careersBundle";
import { useCareerDna } from "@/hooks/useCareerDna";
import { loadCareerIntel, type CareerIntel } from "@/lib/careerIntel";
import { buildGrowthLadder } from "@/lib/growthLadder";

const CURATED = [
  "Pandit for Rituals", "Hindu Priest (Pandit)", "Vegetable Vendor (Push Cart)", "Dabbawala",
  "Street Food Vendor", "Astrologer (Jyotish)", "Coconut Water Vendor", "Flower Seller (Street)",
  "Handloom Weaver", "Pottery Artisan", "Toddy Tapper", "Pashmina Weaver", "Carpet Weaver (Kashmir)",
  "Cobbler (Shoe Repair)", "Nai (Traditional Barber)", "Blacksmith", "Tattoo Artist", "Street Artist",
  "Puppeteer", "Circus Performer", "Clown", "Stunt Performer", "Foley Artist", "Sommelier",
  "Beekeeping Specialist", "Juice Stall Operator", "Village Milk Collection Agent", "Street Performer",
  "Exoplanet Hunter", "Bug Bounty Hunter", "Locksmith", "E-Rickshaw Driver",
];

export default function UnconventionalRoles() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<CareerRow[]>([]);
  const { data: dna } = useCareerDna();
  const primary = dna?.archetypePrimary ?? null;

  useEffect(() => {
    if (rows.length) return;
    loadCareersBundle()
      .then((b) => {
        const byName = new Map(b.rows.map((r) => [r.name, r]));
        setRows(CURATED.map((n) => byName.get(n)).filter(Boolean) as CareerRow[]);
      })
      .catch(() => setRows([]));
  }, [rows.length]);

  const sorted = useMemo(() => {
    if (!primary) return [];
    return rows.filter((r) => r.archetypes?.some((a) => a.name === primary));
  }, [rows, primary]);

  const [intel, setIntel] = useState<Record<string, CareerIntel | null>>({});
  useEffect(() => {
    if (!open) return;
    const missing = sorted.filter((r) => !(r.ciId in intel));
    if (!missing.length) return;
    Promise.all(missing.map((r) => loadCareerIntel(r.name).catch(() => null).then((i) => [r.ciId, i] as const)))
      .then((pairs) => setIntel((prev) => ({ ...prev, ...Object.fromEntries(pairs) })));
  }, [open, sorted, intel]);

  // Only show this section when at least one role fits the test taker.
  if (sorted.length === 0) return null;

  return (
    <section className="calm-card p-6 sm:p-8" aria-labelledby="unconventional-heading">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="unconventional-panel"
        className="flex min-h-[48px] w-full items-center justify-between gap-4 rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span>
          <span id="unconventional-heading" className="font-display text-lg font-semibold text-foreground">
            Unconventional Roles
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            Off-the-beaten-path work that might pique your interest.
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-primary transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div id="unconventional-panel" className="mt-5 animate-fade-in motion-reduce:animate-none">
          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">Gathering a few surprising roles…</p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {sorted.map((r) => {
                const shared = primary && r.archetypes?.some((a) => a.name === primary);
                return (
                  <li key={r.ciId}>
                    <Link
                      to={`/careers/${encodeURIComponent(r.ciId)}`}
                      className="calm-card-quiet flex h-full min-h-[96px] flex-col gap-1.5 p-4 transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {shared && (
                        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/12 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-primary">
                          <Compass className="h-3 w-3" aria-hidden="true" /> Shares your {primary} side
                        </span>
                      )}
                      <span className="font-display text-base font-semibold text-foreground">{r.name}</span>
                      <span className="text-xs text-muted-foreground">{r.family}</span>
                      {(() => {
                        const i = intel[r.ciId];
                        if (!i) return null;
                        const steps = buildGrowthLadder({ name: r.name, level: i.level, stretch: i.stretch, entrepreneurship: i.entrepreneurship });
                        if (steps.length < 2) return null;
                        return (
                          <ol className="mt-1 flex flex-wrap items-center gap-1 text-xs text-foreground/80" aria-label={`Growth path for ${r.name}`}>
                            {steps.map((st, k) => (
                              <li key={st.name} className="inline-flex items-center gap-1">
                                {k > 0 && <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden="true" />}
                                <span className={k === 0 ? "font-medium text-primary" : ""}>{k === 0 ? "Start here" : st.name}</span>
                              </li>
                            ))}
                          </ol>
                        );
                      })()}
                      <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                        See the full path <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
